﻿import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, Key, Eye, EyeOff, Check, AlertCircle, Loader2, 
  Plus, Trash2, Settings, Sparkles, Bot, Zap, Globe,
  ChevronDown, ChevronRight, Copy, RefreshCw, Shield,
  Server, Cpu, Brain, Star, ExternalLink, Info, BarChart3,
  TrendingUp, Users, Calendar, Activity
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { saveMinimaxToServerDatabase } from '../../services/minimaxApi';
import {
  extractMinimaxAssistantContent,
  buildMinimaxEmptyBodyHint,
  getMinimaxBusinessError,
  formatMinimaxBusinessErrorHint,
} from '../../utils/minimaxResponseParse.js';
import { resolveMinimaxChatCompletionUrl } from '../../utils/minimaxEndpoint.js';

// 预设的 AI 服务提供商 - 增强版
const AI_PROVIDERS = [
  {
    id: 'openai',
    name: 'OpenAI',
    icon: '🤖',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg',
    baseUrl: 'https://api.openai.com/v1',
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo'],
    description: 'ChatGPT 系列模型',
    color: '#10a37f',
    gradient: 'from-emerald-500 to-teal-600',
    popular: true
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    icon: '🧠',
    baseUrl: 'https://api.anthropic.com/v1',
    models: ['claude-3-5-sonnet-20241022', 'claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'],
    description: 'Claude 系列模型',
    color: '#d97706',
    gradient: 'from-amber-500 to-orange-600',
    popular: true
  },
  {
    id: 'deepseek',
    name: 'DeepSeek',
    icon: '🔮',
    baseUrl: 'https://api.deepseek.com/v1',
    models: ['deepseek-chat', 'deepseek-coder', 'deepseek-reasoner'],
    description: '深度求索 AI',
    color: '#6366f1',
    gradient: 'from-indigo-500 to-purple-600',
    popular: true
  },
  {
    id: 'qwen',
    name: '通义千问',
    icon: '🌟',
    baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    models: ['qwen-turbo', 'qwen-plus', 'qwen-max', 'qwen-long'],
    description: '阿里云通义千问',
    color: '#ff6a00',
    gradient: 'from-orange-500 to-red-500'
  },
  {
    id: 'bailian',
    name: '百炼大模型',
    icon: '⚡',
    baseUrl: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
    models: [
      'qwen-max',
      'qwen-max-longcontext', 
      'qwen-plus',
      'qwen-turbo',
      'qwen-vl-max',
      'qwen-vl-plus',
      'qwen2.5-72b-instruct',
      'qwen2.5-32b-instruct',
      'qwen2.5-14b-instruct',
      'qwen2.5-7b-instruct'
    ],
    description: '阿里云百炼平台',
    color: '#ff6600',
    gradient: 'from-orange-600 to-red-600',
    popular: true
  },
  {
    id: 'zhipu',
    name: '智谱 AI',
    icon: '💎',
    baseUrl: 'https://open.bigmodel.cn/api/paas/v4',
    models: ['glm-4-plus', 'glm-4', 'glm-4-flash', 'glm-4-long'],
    description: '智谱清言 GLM 系列',
    color: '#3b82f6',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    id: 'moonshot',
    name: 'Moonshot',
    icon: '🌙',
    baseUrl: 'https://api.moonshot.cn/v1',
    models: ['moonshot-v1-8k', 'moonshot-v1-32k', 'moonshot-v1-128k'],
    description: 'Kimi 智能助手',
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-purple-600'
  },
  {
    id: 'minimax',
    name: 'Minimax',
    icon: '✨',
    baseUrl: 'https://api.minimaxi.com/v1',
    models: ['MiniMax-M2.7', 'MiniMax-M2.7-highspeed', 'MiniMax-M2.5', 'MiniMax-M2.5-highspeed', 'MiniMax-M2.1', 'MiniMax-M2'],
    description: 'MiniMax 开放平台（OpenAI 兼容，默认 M2.7）',
    color: '#7c3aed',
    gradient: 'from-violet-600 to-purple-700'
  },
  {
    id: 'volcano',
    name: '火山引擎 Coding Plan',
    icon: '🌋',
    baseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
    models: [],
    description: '火山方舟大模型（Coding Plan 优惠）',
    color: '#ff6b00',
    gradient: 'from-orange-600 to-red-600'
  },
  {
    id: 'ollama',
    name: 'Ollama',
    icon: '🦙',
    baseUrl: 'http://localhost:11434/v1',
    models: ['llama3', 'qwen2', 'deepseek-coder', 'mistral'],
    description: '本地部署模型',
    color: '#374151',
    gradient: 'from-gray-600 to-gray-800'
  },
  {
    id: 'custom',
    name: '自定义',
    icon: '⚙️',
    baseUrl: '',
    models: [],
    description: '自定义 API 端点',
    color: '#6b7280',
    gradient: 'from-gray-500 to-gray-600'
  }
];

// 存储键
const STORAGE_KEY = 'ai_api_configs';
const DEFAULT_PROVIDER_KEY = 'ai_default_provider';
const ENABLED_FEATURES_KEY = 'ai_enabled_features';
const USAGE_STATS_KEY = 'ai_usage_stats'; // 用量统计

/** 将大模型 API 配置中的 Minimax 同步到服务端，供舆情情感分析、AI 摘要等使用 */
function syncMinimaxToServer(configs) {
  const minimax = Array.isArray(configs) ? configs.find(c => (c.providerId || c.provider) === 'minimax') : null;
  const payload = minimax && (minimax.apiKey || minimax.api_key)
    ? {
        apiKey: minimax.apiKey || minimax.api_key,
        groupId: '',
        model: minimax.model || minimax.selectedModel || 'MiniMax-M2.7',
        baseUrl: (minimax.baseUrl || minimax.endpoint || 'https://api.minimaxi.com/v1').replace(/\/+$/, ''),
      }
    : { apiKey: '' };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '';
  fetch('/api/ai-config/minimax', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
    credentials: 'include',
  }).then((res) => {
    if (res.ok) return res.json();
    return Promise.reject(new Error(res.statusText));
  }).then(() => {
    console.log('Minimax 配置已同步到服务端，舆情情感分析将使用此配置');
  }).catch((e) => {
    console.warn('同步 Minimax 到服务端失败（舆情情感分析将使用环境变量或未配置）:', e.message);
  });
}

/** 从服务端恢复 Minimax 配置（清理浏览器缓存后可用，避免重新填写） */
async function restoreMinimaxFromServer() {
  try {
    const res = await fetch('/api/ai-config/minimax?restore=1', { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data || !data.configured || !data.apiKey) return null;
    return {
      providerId: 'minimax',
      apiKey: data.apiKey,
      groupId: '',
      model: data.model || 'MiniMax-M2.7',
      baseUrl: (data.baseUrl || 'https://api.minimaxi.com/v1').replace(/\/+$/, ''),
    };
  } catch (e) {
    console.warn('从服务端恢复 Minimax 配置失败:', e.message);
    return null;
  }
}

// 可启用 AI 的功能模块 - 对应审计专栏实际功能
const AI_FEATURES = [
  { id: 'brainstorm', name: '头脑风暴', description: 'AI智能生成风险与机会', icon: '💡', color: 'yellow' },
  { id: 'risk-matrix', name: '风险矩阵', description: '风险与机会识别与智能评估', icon: '🎯', color: 'red' },
  { id: 'project-manager', name: '项目管理', description: '审计项目智能建议', icon: '📋', color: 'blue' },
  { id: 'internal-control', name: '内部控制', description: '内控测试智能分析', icon: '🐔', color: 'sky' },
  { id: 'duties', name: '职责分离', description: '不相容职责智能识别', icon: '🛡️', color: 'purple' },
  { id: 'ippf', name: 'IPPF框架', description: '审计人员能力评估', icon: '📚', color: 'green' },
  { id: 'futures-trader', name: 'AI期货分析', description: '期货行情智能分析', icon: '📈', color: 'amber' },
  { id: 'doc-analyzer', name: '智能文档', description: 'AI驱动文档分析', icon: '📄', color: 'violet' },
  { id: 'product-cost', name: '成本穿透', description: '成本数据智能分析', icon: '💰', color: 'cyan' },
  { id: 'model-library', name: '模型库', description: '分析模型智能推荐', icon: '🧮', color: 'emerald' },
  { id: 'ocrmypdf', name: 'OCR识别', description: 'PDF智能文字提取', icon: '🔍', color: 'orange' },
  { id: 'group-permission-library', name: '集团权限库', description: '权限矩阵智能审核与修改建议', icon: '🔐', color: 'indigo' },
];

// 导出的工具函数
export const getAIConfigs = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
};

export const getDefaultProvider = () => {
  try { return localStorage.getItem(DEFAULT_PROVIDER_KEY) || null; }
  catch { return null; }
};

export const getEnabledFeatures = () => {
  try {
    const saved = localStorage.getItem(ENABLED_FEATURES_KEY);
    return saved ? JSON.parse(saved) : AI_FEATURES.map(f => f.id);
  } catch { return AI_FEATURES.map(f => f.id); }
};

export const isFeatureAIEnabled = (featureId) => getEnabledFeatures().includes(featureId);
export const getProviderConfig = (providerId) => getAIConfigs().find(c => c.providerId === providerId);
export const getActiveAIConfig = () => {
  const defaultId = getDefaultProvider();
  return defaultId ? getProviderConfig(defaultId) : null;
};
export const getAIConfigForFeature = (featureId) => isFeatureAIEnabled(featureId) ? getActiveAIConfig() : null;

// 用量统计相关函数
export const getUsageStats = () => {
  try {
    const saved = localStorage.getItem(USAGE_STATS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

export const recordUsage = (providerId, model, tokens, feature = 'unknown') => {
  try {
    const stats = getUsageStats();
    const today = new Date().toISOString().split('T')[0];
    const username = localStorage.getItem('current_user') || 'guest';
    
    if (!stats[username]) stats[username] = {};
    if (!stats[username][providerId]) stats[username][providerId] = {};
    if (!stats[username][providerId][today]) {
      stats[username][providerId][today] = { requests: 0, tokens: 0, features: {}, models: {} };
    }
    
    const dayStats = stats[username][providerId][today];
    dayStats.requests += 1;
    dayStats.tokens += tokens;
    dayStats.features[feature] = (dayStats.features[feature] || 0) + 1;
    dayStats.models[model] = (dayStats.models[model] || 0) + 1;
    
    localStorage.setItem(USAGE_STATS_KEY, JSON.stringify(stats));
    window.dispatchEvent(new CustomEvent('ai-usage-updated'));
  } catch (e) {
    console.error('记录用量失败:', e);
  }
};

// 主组件
const AIAPIConfig = ({ isOpen, onClose, embedded = false }) => {
  const { colors } = useTheme();
  const { isMobile } = useBreakpoint();
  const [configs, setConfigs] = useState([]);
  const [defaultProvider, setDefaultProvider] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testDetails, setTestDetails] = useState(null); // 详细测试结果
  const [expandedId, setExpandedId] = useState(null);
  const [activeTab, setActiveTab] = useState('providers');
  const [enabledFeatures, setEnabledFeatures] = useState([]);
  const [copiedKey, setCopiedKey] = useState(null);
  const [usageStats, setUsageStats] = useState({});
  const [selectedUser, setSelectedUser] = useState('all');

  useEffect(() => {
    const list = getAIConfigs();
    setConfigs(list);
    setDefaultProvider(getDefaultProvider());
    setEnabledFeatures(getEnabledFeatures());
    setUsageStats(getUsageStats());
    if (isOpen) {
      syncMinimaxToServer(list);
      const hasLocalMinimax = list.some(c => (c.providerId || c.provider) === 'minimax');
      if (!hasLocalMinimax) {
        restoreMinimaxFromServer().then((serverMinimax) => {
          if (serverMinimax) {
            const merged = [...list, { ...serverMinimax, name: 'Minimax' }];
            localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
            setConfigs(merged);
            window.dispatchEvent(new CustomEvent('ai-config-updated', { detail: merged }));
          }
        });
      }
    }
    const handleUsageUpdate = () => setUsageStats(getUsageStats());
    window.addEventListener('ai-usage-updated', handleUsageUpdate);
    return () => window.removeEventListener('ai-usage-updated', handleUsageUpdate);
  }, [isOpen]);

  // 当切换到用量统计Tab时刷新数据
  useEffect(() => {
    if (activeTab === 'usage') {
      setUsageStats(getUsageStats());
    }
  }, [activeTab]);

  const saveConfigs = useCallback((newConfigs) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfigs));
    setConfigs(newConfigs);
    window.dispatchEvent(new CustomEvent('ai-config-updated', { detail: newConfigs }));
    syncMinimaxToServer(newConfigs);
  }, []);

  const saveEnabledFeatures = useCallback((features) => {
    localStorage.setItem(ENABLED_FEATURES_KEY, JSON.stringify(features));
    setEnabledFeatures(features);
    window.dispatchEvent(new CustomEvent('ai-config-updated'));
  }, []);

  const toggleFeature = useCallback((featureId) => {
    const newFeatures = enabledFeatures.includes(featureId)
      ? enabledFeatures.filter(f => f !== featureId)
      : [...enabledFeatures, featureId];
    saveEnabledFeatures(newFeatures);
  }, [enabledFeatures, saveEnabledFeatures]);

  const toggleAllFeatures = useCallback(() => {
    saveEnabledFeatures(enabledFeatures.length === AI_FEATURES.length ? [] : AI_FEATURES.map(f => f.id));
  }, [enabledFeatures, saveEnabledFeatures]);

  const handleSetDefault = useCallback((providerId) => {
    localStorage.setItem(DEFAULT_PROVIDER_KEY, providerId);
    setDefaultProvider(providerId);
    window.dispatchEvent(new CustomEvent('ai-config-updated'));
  }, []);

  const handleDelete = useCallback((providerId) => {
    if (!confirm('确定要删除这个 API 配置吗？')) return;
    const newConfigs = configs.filter(c => c.providerId !== providerId);
    saveConfigs(newConfigs);
    if (defaultProvider === providerId) {
      localStorage.removeItem(DEFAULT_PROVIDER_KEY);
      setDefaultProvider(null);
    }
  }, [configs, defaultProvider, saveConfigs]);

  // 强化版测试连接
  const handleTest = useCallback(async (config) => {
    setTestingId(config.providerId);
    setTestResult(null);
    setTestDetails(null);
    
    const startTime = Date.now();
    const details = {
      providerId: config.providerId,
      model: config.model,
      baseUrl: config.baseUrl,
      steps: [],
      startTime: new Date().toLocaleTimeString()
    };

    try {
      // 步骤1: 检查网络连接
      details.steps.push({ name: '检查网络', status: 'running' });
      setTestDetails({ ...details });
      
      // 步骤2: 发送测试请求（Minimax 走后端代理避免 CORS）
      details.steps[0] = { name: '检查网络', status: 'success', time: Date.now() - startTime };
      details.steps.push({ name: '发送请求', status: 'running' });
      setTestDetails({ ...details });

      const isMinimax = config.providerId === 'minimax';
      const isVolcano = config.providerId === 'volcano';
      const testMsg = { role: 'user', content: '你好，请用一句话介绍自己' };
      let response;
      let data;

      if (isMinimax) {
        const appToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        if (!appToken) {
          throw new Error('请先登录系统后再测试（缺少本系统认证令牌）');
        }
        const minimaxUrl = resolveMinimaxChatCompletionUrl(config.baseUrl || '');
        const proxyRes = await fetch('/api/proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${appToken}` },
          body: JSON.stringify({
            url: minimaxUrl,
            method: 'POST',
            headers: { Authorization: `Bearer ${config.apiKey}` },
            body: {
              model: config.model || 'MiniMax-M2.7',
              messages: [testMsg],
              temperature: 0.7,
              max_completion_tokens: 50,
            },
          }),
        });
        const proxyJson = await proxyRes.json().catch(() => ({}));
        if (!proxyRes.ok) {
          throw new Error(proxyJson.error || proxyRes.statusText || '代理请求失败');
        }
        if (proxyJson.status !== 200) {
          data = proxyJson.data;
          response = { ok: false, status: proxyJson.status, statusText: proxyJson.statusText };
        } else {
          data = proxyJson.data;
          response = { ok: true };
        }
      } else if (isVolcano) {
        const appToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
        if (!appToken) {
          throw new Error('请先登录系统后再测试（缺少本系统认证令牌）');
        }
        const volcanoUrl = (config.baseUrl || 'https://ark.cn-beijing.volces.com/api/v3').replace(/\/+$/, '') + '/chat/completions';
        const proxyRes = await fetch('/api/proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${appToken}` },
          body: JSON.stringify({
            url: volcanoUrl,
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${config.apiKey}` },
            body: {
              model: config.model || '',
              messages: [testMsg],
              max_tokens: 50,
              stream: false,
            },
          }),
        });
        const proxyJson = await proxyRes.json().catch(() => ({}));
        if (!proxyRes.ok) {
          throw new Error(proxyJson.error || proxyRes.statusText || '代理请求失败');
        }
        if (proxyJson.status !== 200) {
          data = proxyJson.data;
          response = { ok: false, status: proxyJson.status, statusText: proxyJson.statusText };
        } else {
          data = proxyJson.data;
          response = { ok: true };
        }
      } else {
        response = await fetch(`${config.baseUrl}/chat/completions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.apiKey}`,
          },
          body: JSON.stringify({
            model: config.model || 'gpt-3.5-turbo',
            messages: [testMsg],
            max_tokens: 50,
            temperature: 0.7,
          }),
        });
        data = response.ok ? await response.json() : await response.json().catch(() => ({}));
      }

      const requestTime = Date.now() - startTime;
      details.steps[1] = { name: '发送请求', status: 'success', time: requestTime };
      details.steps.push({ name: '解析响应', status: 'running' });
      setTestDetails({ ...details });

      const totalTime = Date.now() - startTime;

      if (isMinimax && data) {
        const bizErr = getMinimaxBusinessError(data);
        if (bizErr) {
          details.steps[2] = { name: '解析响应', status: 'error', time: totalTime };
          const msg = formatMinimaxBusinessErrorHint(bizErr);
          details.error = {
            message: msg,
            code: bizErr.code,
          };
          details.response = {
            content:
              typeof data === 'object' && data !== null
                ? JSON.stringify(data)
                : String(data),
            latency: totalTime,
          };
          details.success = false;
          setTestDetails(details);
          setTestResult({
            success: false,
            message: msg,
            details,
          });
          return;
        }
      }

      if (response.ok) {
        details.steps[2] = { name: '解析响应', status: 'success', time: totalTime };
        const minimaxContent =
          isMinimax &&
          (extractMinimaxAssistantContent(data) ||
            buildMinimaxEmptyBodyHint(data) ||
            '（正文为空：请核对模型名、API Key、余额或是否在控制台开启了对应模型）');
        details.response = {
          model: data.model,
          content: isMinimax
            ? minimaxContent
            : data.choices?.[0]?.message?.content || data.reply || '无响应内容',
          tokens: {
            prompt: data.usage?.prompt_tokens,
            completion: data.usage?.completion_tokens,
            total: data.usage?.total_tokens,
          },
          latency: totalTime,
        };
        details.success = true;
        setTestDetails(details);
        setTestResult({
          success: true,
          message: `连接成功 (${totalTime}ms)`,
          details,
        });
      } else {
        details.steps[2] = { name: '解析响应', status: 'error', time: totalTime };
        const errMsg = data?.base_resp?.status_msg || data?.error?.message || data?.message || '未知错误';
        const isAuthError = /认证|令牌|token|unauthorized/i.test(errMsg);
        const hint = isAuthError && isMinimax
          ? ' 请确认 API Key 来自 MiniMax 开放平台「接口密钥」，且端点为 OpenAI 兼容地址（默认 https://api.minimaxi.com/v1）。'
          : '';
        details.error = {
          status: response.status,
          statusText: response.statusText,
          message: errMsg,
          code: data?.error?.code || data?.base_resp?.status_code,
          type: data?.error?.type,
        };
        details.success = false;
        setTestDetails(details);
        setTestResult({
          success: false,
          message: errMsg + hint,
          details,
        });
      }
    } catch (err) {
      const totalTime = Date.now() - startTime;
      details.steps[details.steps.length - 1] = { 
        name: details.steps[details.steps.length - 1]?.name || '连接', 
        status: 'error', 
        time: totalTime 
      };
      details.error = {
        message: err.message || '网络连接失败',
        type: 'NetworkError',
        details: err.toString()
      };
      details.success = false;
      
      setTestDetails(details);
      setTestResult({ 
        success: false, 
        message: `连接失败: ${err.message}`,
        details 
      });
    } finally {
      setTestingId(null);
    }
  }, []);

  const handleCopyKey = useCallback((apiKey, providerId) => {
    navigator.clipboard.writeText(apiKey);
    setCopiedKey(providerId);
    setTimeout(() => setCopiedKey(null), 2000);
  }, []);

  if (!isOpen && !embedded) return null;

  return (
    <div className={embedded ? "h-full flex flex-col min-h-0" : "fixed inset-0 bg-black/60 flex items-center justify-center z-50"} onClick={!embedded ? onClose : undefined}>
      <div className={embedded ? "h-full flex flex-col min-h-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50" : "bg-white rounded-2xl shadow-2xl w-[800px] max-h-[90vh] overflow-hidden"} onClick={e => e.stopPropagation()}>
        
        {/* 头部 - 渐变背景 */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600" />
          <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
          <div className={`relative flex items-center justify-between ${isMobile ? 'px-3 py-3' : 'px-6 py-5'}`}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/25 flex items-center justify-center shadow-lg">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  大模型 API 配置
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-normal">全局</span>
                </h2>
                <p className="text-white/70 text-sm mt-0.5">配置 AI 服务，为应用提供智能能力</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {configs.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/15 rounded-lg">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-white/90 text-sm">{configs.length} 个服务已配置</span>
                </div>
              )}
              {!embedded && (
                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
                  <X className="w-5 h-5 text-white" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab 切换 */}
        <div className={`bg-white border-b border-gray-100 ${isMobile ? 'px-3 py-3' : 'px-6 py-4'}`}>
          <div className="flex gap-2 p-1 bg-gray-100/80 rounded-xl">
            <button
              onClick={() => setActiveTab('providers')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === 'providers' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Server className="w-4 h-4" />
              服务配置
            </button>
            <button
              onClick={() => setActiveTab('features')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === 'features' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Zap className="w-4 h-4" />
              功能开关
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${activeTab === 'features' ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-500'}`}>
                {enabledFeatures.length}/{AI_FEATURES.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === 'usage' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              用量统计
            </button>
          </div>
        </div>

        {/* 内容区 */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'providers' && (
            <div className="space-y-6">
              {/* 已配置的服务列表 */}
              {configs.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                    <Bot className="w-10 h-10 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">尚未配置 AI 服务</h3>
                  <p className="text-gray-400 text-sm mb-6">添加您的 API 密钥，开启智能功能</p>
                  <button
                    onClick={() => { setEditingConfig(null); setShowAddDialog(true); }}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all shadow-lg shadow-purple-200"
                  >
                    <Plus className="w-4 h-4" />
                    添加第一个服务
                  </button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {configs.map((config) => {
                    const provider = AI_PROVIDERS.find(p => p.id === config.providerId) || AI_PROVIDERS.find(p => p.id === 'custom');
                    const isExpanded = expandedId === config.providerId;
                    const isDefault = defaultProvider === config.providerId;
                    
                    return (
                      <div key={config.providerId} className={`bg-white rounded-2xl border-2 transition-all overflow-hidden ${isDefault ? 'border-purple-300 shadow-lg shadow-purple-100' : 'border-gray-100 hover:border-gray-200 hover:shadow-md'}`}>
                        <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : config.providerId)}>
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${provider.gradient} flex items-center justify-center text-2xl shadow-lg`}>
                            {provider.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-gray-800 text-lg">{provider.name}</span>
                              {isDefault && (
                                <span className="px-2.5 py-1 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs rounded-full font-medium flex items-center gap-1">
                                  <Star className="w-3 h-3" /> 默认
                                </span>
                              )}
                              {provider.popular && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">热门</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                              <span className="flex items-center gap-1"><Cpu className="w-3.5 h-3.5" />{config.model || provider.models?.[0]}</span>
                              <span className="text-gray-300">|</span>
                              <span className="truncate max-w-[200px]">{config.baseUrl}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {testingId === config.providerId && <Loader2 className="w-5 h-5 text-purple-500 animate-spin" />}
                            {testResult && expandedId === config.providerId && (
                              <span className={`px-2 py-1 rounded-lg text-xs font-medium ${testResult.success ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                {testResult.message}
                              </span>
                            )}
                            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-gray-100 pt-4 space-y-4 bg-gray-50/50">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">API Key</label>
                                <div className="flex items-center gap-2">
                                  <code className="flex-1 px-4 py-2.5 bg-white rounded-xl text-sm font-mono text-gray-600 border border-gray-200">
                                    {config.apiKey.slice(0, 10)}••••••••{config.apiKey.slice(-4)}
                                  </code>
                                  <button onClick={() => handleCopyKey(config.apiKey, config.providerId)} className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors" title="复制">
                                    {copiedKey === config.providerId ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-400" />}
                                  </button>
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">API 端点</label>
                                <code className="block px-4 py-2.5 bg-white rounded-xl text-sm font-mono text-gray-600 border border-gray-200 truncate">
                                  {config.baseUrl}
                                </code>
                              </div>
                            </div>

                            {/* 详细测试结果 */}
                            {testDetails && testDetails.providerId === config.providerId && (
                              <div className={`p-4 rounded-xl border-2 ${testDetails.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className={`font-semibold flex items-center gap-2 ${testDetails.success ? 'text-green-700' : 'text-red-700'}`}>
                                    {testDetails.success ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                    测试结果
                                  </h4>
                                  <span className="text-xs text-gray-500">{testDetails.startTime}</span>
                                </div>

                                {/* 测试步骤 */}
                                <div className="space-y-2 mb-3">
                                  {testDetails.steps.map((step, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-sm">
                                      {step.status === 'running' && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
                                      {step.status === 'success' && <Check className="w-4 h-4 text-green-500" />}
                                      {step.status === 'error' && <X className="w-4 h-4 text-red-500" />}
                                      <span className="text-gray-700">{step.name}</span>
                                      {step.time && <span className="text-gray-400 text-xs ml-auto">{step.time}ms</span>}
                                    </div>
                                  ))}
                                </div>

                                {/* 成功响应详情 */}
                                {testDetails.success && testDetails.response && (
                                  <div className="space-y-2 pt-3 border-t border-green-200">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-green-600 font-medium">模型响应</span>
                                      <span className="text-green-600">{testDetails.response.model}</span>
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-green-200">
                                      <p className="text-sm text-gray-700 italic">"{testDetails.response.content}"</p>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs text-green-600">
                                      <span>延迟: {testDetails.response.latency}ms</span>
                                      {testDetails.response.tokens.total && (
                                        <span>Token: {testDetails.response.tokens.total} ({testDetails.response.tokens.prompt}+{testDetails.response.tokens.completion})</span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* 错误详情 */}
                                {!testDetails.success && testDetails.error && (
                                  <div className="space-y-2 pt-3 border-t border-red-200">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-red-600 font-medium">错误信息</span>
                                      {testDetails.error.status && <span className="text-red-600">HTTP {testDetails.error.status}</span>}
                                    </div>
                                    <div className="p-3 bg-white rounded-lg border border-red-200">
                                      <p className="text-sm text-red-700">{testDetails.error.message}</p>
                                      {testDetails.error.code && (
                                        <p className="text-xs text-red-500 mt-1">错误代码: {testDetails.error.code}</p>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2 pt-2">
                              <button onClick={() => handleTest(config)} disabled={testingId === config.providerId}
                                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl text-sm font-medium hover:from-blue-600 hover:to-cyan-600 transition-all disabled:opacity-50 shadow-sm">
                                <RefreshCw className={`w-4 h-4 ${testingId === config.providerId ? 'animate-spin' : ''}`} />
                                {testingId === config.providerId ? '测试中...' : '测试连接'}
                              </button>
                              {!isDefault && (
                                <button onClick={() => handleSetDefault(config.providerId)}
                                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl text-sm font-medium hover:from-purple-600 hover:to-indigo-600 transition-all shadow-sm">
                                  <Star className="w-4 h-4" />
                                  设为默认
                                </button>
                              )}
                              <button onClick={() => { setEditingConfig(config); setShowAddDialog(true); }}
                                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors">
                                <Settings className="w-4 h-4" />
                                编辑
                              </button>
                              <button onClick={() => handleDelete(config.providerId)}
                                className="flex items-center gap-2 px-4 py-2 bg-white border border-red-200 text-red-500 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors ml-auto">
                                <Trash2 className="w-4 h-4" />
                                删除
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* 添加按钮 */}
              {configs.length > 0 && (
                <button onClick={() => { setEditingConfig(null); setShowAddDialog(true); }}
                  className="w-full py-5 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:border-purple-300 hover:text-purple-500 hover:bg-purple-50/50 transition-all flex items-center justify-center gap-2 group">
                  <Plus className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="font-medium">添加新的 AI 服务</span>
                </button>
              )}

              {/* 提示卡片 */}
              <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200/50">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Info className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-2">使用说明</h4>
                    <ul className="text-sm space-y-1.5 text-amber-700">
                      <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" />配置的 API 将在应用内的 AI 功能中使用</li>
                      <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" />设置为"默认"的服务将优先被调用</li>
                      <li className="flex items-center gap-2"><Shield className="w-3.5 h-3.5" />API Key 仅保存在本地，不会上传服务器</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 功能开关 Tab */}
          {activeTab === 'features' && (
            <div className="space-y-6">
              {/* 全选控制 */}
              <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">AI 功能开关</p>
                    <p className="text-xs text-gray-500">选择需要启用 AI 能力的功能模块</p>
                  </div>
                </div>
                <button onClick={toggleAllFeatures}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    enabledFeatures.length === AI_FEATURES.length
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 shadow-lg shadow-purple-200'
                  }`}>
                  {enabledFeatures.length === AI_FEATURES.length ? '取消全选' : '全部启用'}
                </button>
              </div>

              {/* 功能网格 */}
              <div className="grid grid-cols-3 gap-4">
                {AI_FEATURES.map((feature) => {
                  const isEnabled = enabledFeatures.includes(feature.id);
                  const colorMap = {
                    yellow: 'from-yellow-500 to-amber-500 border-yellow-200 bg-yellow-50',
                    blue: 'from-blue-500 to-cyan-500 border-blue-200 bg-blue-50',
                    red: 'from-red-500 to-pink-500 border-red-200 bg-red-50',
                    green: 'from-green-500 to-emerald-500 border-green-200 bg-green-50',
                    purple: 'from-purple-500 to-violet-500 border-purple-200 bg-purple-50',
                    amber: 'from-amber-500 to-orange-500 border-amber-200 bg-amber-50',
                    cyan: 'from-cyan-500 to-teal-500 border-cyan-200 bg-cyan-50',
                    indigo: 'from-indigo-500 to-blue-500 border-indigo-200 bg-indigo-50',
                    pink: 'from-pink-500 to-rose-500 border-pink-200 bg-pink-50',
                    sky: 'from-sky-500 to-blue-500 border-sky-200 bg-sky-50',
                    violet: 'from-violet-500 to-purple-500 border-violet-200 bg-violet-50',
                    emerald: 'from-emerald-500 to-green-500 border-emerald-200 bg-emerald-50',
                    orange: 'from-orange-500 to-amber-500 border-orange-200 bg-orange-50',
                  };
                  const colors = colorMap[feature.color] || colorMap.blue;
                  const [gradient, border, bg] = colors.split(' ');
                  
                  return (
                    <div key={feature.id} onClick={() => toggleFeature(feature.id)}
                      className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                        isEnabled ? `${border} ${bg} shadow-md` : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
                      }`}>
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                          isEnabled ? `bg-gradient-to-br ${gradient} shadow-lg` : 'bg-gray-100'
                        }`}>
                          {isEnabled ? <span className="filter drop-shadow">{feature.icon}</span> : feature.icon}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-semibold ${isEnabled ? 'text-gray-800' : 'text-gray-600'}`}>{feature.name}</span>
                            {isEnabled && (
                              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 未配置提示 */}
              {configs.length === 0 && (
                <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200/50">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-amber-800 mb-1">请先配置 AI 服务</h4>
                      <p className="text-sm text-amber-700">切换到"服务配置"标签页添加 API 密钥后，这些功能才能正常使用</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 用量统计 Tab */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              {/* 用户选择器 */}
              <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">用量统计</p>
                    <p className="text-xs text-gray-500">查看各用户的大模型使用情况</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setUsageStats(getUsageStats())}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="刷新数据"
                  >
                    <RefreshCw className="w-4 h-4 text-gray-600" />
                  </button>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">全部用户</option>
                    {Object.keys(usageStats).map(username => (
                      <option key={username} value={username}>{username}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 统计卡片 */}
              {Object.keys(usageStats).length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-200">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                    <BarChart3 className="w-10 h-10 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">暂无用量数据</h3>
                  <p className="text-gray-400 text-sm">开始使用 AI 功能后，这里将显示详细的用量统计</p>
                </div>
              ) : (
                <>
                  {/* 汇总统计 */}
                  {(() => {
                    const filteredStats = selectedUser === 'all' ? usageStats : { [selectedUser]: usageStats[selectedUser] };
                    let totalRequests = 0, totalTokens = 0;
                    const providerStats = {};
                    
                    Object.values(filteredStats).forEach(userStats => {
                      Object.entries(userStats).forEach(([providerId, providerData]) => {
                        if (!providerStats[providerId]) providerStats[providerId] = { requests: 0, tokens: 0 };
                        Object.values(providerData).forEach(dayData => {
                          totalRequests += dayData.requests;
                          totalTokens += dayData.tokens;
                          providerStats[providerId].requests += dayData.requests;
                          providerStats[providerId].tokens += dayData.tokens;
                        });
                      });
                    });

                    return (
                      <>
                        {/* 总览卡片 */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-5 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-blue-600">总请求数</span>
                              <Activity className="w-5 h-5 text-blue-400" />
                            </div>
                            <p className="text-3xl font-bold text-blue-700">{totalRequests.toLocaleString()}</p>
                            <p className="text-xs text-blue-500 mt-1">API 调用次数</p>
                          </div>
                          <div className="p-5 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-purple-600">总 Token 数</span>
                              <TrendingUp className="w-5 h-5 text-purple-400" />
                            </div>
                            <p className="text-3xl font-bold text-purple-700">{totalTokens.toLocaleString()}</p>
                            <p className="text-xs text-purple-500 mt-1">累计消耗</p>
                          </div>
                          <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-green-600">活跃用户</span>
                              <Users className="w-5 h-5 text-green-400" />
                            </div>
                            <p className="text-3xl font-bold text-green-700">{Object.keys(filteredStats).length}</p>
                            <p className="text-xs text-green-500 mt-1">使用人数</p>
                          </div>
                        </div>

                        {/* 按服务商统计 */}
                        <div className="bg-white rounded-2xl border border-gray-100 p-5">
                          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <Server className="w-5 h-5 text-gray-600" />
                            按服务商统计
                          </h3>
                          <div className="space-y-3">
                            {Object.entries(providerStats).map(([providerId, stats]) => {
                              const provider = AI_PROVIDERS.find(p => p.id === providerId);
                              const percentage = totalRequests > 0 ? (stats.requests / totalRequests * 100).toFixed(1) : 0;
                              return (
                                <div key={providerId} className="p-4 bg-gray-50 rounded-xl">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${provider?.gradient || 'from-gray-400 to-gray-500'} flex items-center justify-center text-xl`}>
                                        {provider?.icon || '🤖'}
                                      </div>
                                      <div>
                                        <p className="font-semibold text-gray-800">{provider?.name || providerId}</p>
                                        <p className="text-xs text-gray-500">{percentage}% 使用率</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-lg font-bold text-gray-700">{stats.requests.toLocaleString()}</p>
                                      <p className="text-xs text-gray-500">{stats.tokens.toLocaleString()} tokens</p>
                                    </div>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                    <div className={`bg-gradient-to-r ${provider?.gradient || 'from-gray-400 to-gray-500'} h-2 rounded-full transition-all`} style={{ width: `${percentage}%` }} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* 按用户详细统计 */}
                        {selectedUser === 'all' && (
                          <div className="bg-white rounded-2xl border border-gray-100 p-5">
                            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                              <Users className="w-5 h-5 text-gray-600" />
                              用户排行
                            </h3>
                            <div className="space-y-2">
                              {Object.entries(usageStats)
                                .map(([username, userStats]) => {
                                  let userRequests = 0, userTokens = 0;
                                  Object.values(userStats).forEach(providerData => {
                                    Object.values(providerData).forEach(dayData => {
                                      userRequests += dayData.requests;
                                      userTokens += dayData.tokens;
                                    });
                                  });
                                  return { username, requests: userRequests, tokens: userTokens };
                                })
                                .sort((a, b) => b.requests - a.requests)
                                .map((user, index) => (
                                  <div key={user.username} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                                        index === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400 text-white' :
                                        'bg-gray-100 text-gray-600'
                                      }`}>
                                        {index + 1}
                                      </div>
                                      <span className="font-medium text-gray-700">{user.username}</span>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-sm font-semibold text-gray-700">{user.requests} 次</p>
                                      <p className="text-xs text-gray-500">{user.tokens.toLocaleString()} tokens</p>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </div>

        {/* 添加/编辑对话框 */}
        {showAddDialog && (
          <AddConfigDialog
            isOpen={showAddDialog}
            onClose={() => { setShowAddDialog(false); setEditingConfig(null); }}
            onSave={(config) => {
              const existing = configs.findIndex(c => c.providerId === config.providerId);
              const newConfigs = existing >= 0 ? configs.map((c, i) => i === existing ? config : c) : [...configs, config];
              saveConfigs(newConfigs);
              if (configs.length === 0) handleSetDefault(config.providerId);
              setShowAddDialog(false);
              setEditingConfig(null);
            }}
            editingConfig={editingConfig}
            existingProviders={configs.map(c => c.providerId)}
          />
        )}
      </div>
    </div>
  );
};

// 添加配置对话框
const AddConfigDialog = ({ isOpen, onClose, onSave, editingConfig, existingProviders }) => {
  const [selectedProvider, setSelectedProvider] = useState(editingConfig?.providerId || '');
  const [apiKey, setApiKey] = useState(editingConfig?.apiKey || '');
  const [baseUrl, setBaseUrl] = useState(editingConfig?.baseUrl || '');
  const [model, setModel] = useState(editingConfig?.model || '');
  const [showKey, setShowKey] = useState(false);
  const [customModels, setCustomModels] = useState(editingConfig?.customModels || '');
  const [minimaxServerSaving, setMinimaxServerSaving] = useState(false);

  const provider = AI_PROVIDERS.find(p => p.id === selectedProvider);

  useEffect(() => {
    if (provider && !editingConfig) {
      setBaseUrl(provider.baseUrl);
      setModel(provider.models?.[0] || '');
    }
  }, [selectedProvider, provider, editingConfig]);

  const handleSave = () => {
    if (!selectedProvider || !apiKey.trim()) {
      alert('请选择服务商并填写 API Key');
      return;
    }
    if (selectedProvider === 'custom' && !baseUrl.trim()) {
      alert('自定义服务需要填写 API 端点');
      return;
    }
    if (selectedProvider === 'volcano' && !model.trim()) {
      alert('火山引擎需填写推理接入点 ID（在控制台「模型推理」→「推理接入点」中创建后复制 ep-xxx 填入）');
      return;
    }
    onSave({
      providerId: selectedProvider,
      apiKey: apiKey.trim(),
      baseUrl: baseUrl.trim() || provider?.baseUrl || '',
      model: model || provider?.models?.[0] || '',
      models: selectedProvider === 'custom' ? customModels.split(',').map(m => m.trim()).filter(Boolean) : provider?.models || [],
    });
  };

  const handleSaveMinimaxServerOnly = async () => {
    if (selectedProvider !== 'minimax') return;
    if (!apiKey.trim()) {
      alert('请先填写 API Key');
      return;
    }
    setMinimaxServerSaving(true);
    try {
      await saveMinimaxToServerDatabase({
        apiKey: apiKey.trim(),
        groupId: '',
        model: (model || provider?.models?.[0] || 'MiniMax-M2.7').trim(),
        baseUrl: (baseUrl.trim() || provider?.baseUrl || 'https://api.minimaxi.com/v1').replace(/\/+$/, ''),
      });
      alert('MiniMax 已写入服务器端配置（ai-minimax-config.json），舆情与后端将使用此配置');
    } catch (e) {
      alert(e?.message || '保存失败');
    } finally {
      setMinimaxServerSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-[600px] max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* 对话框头部 */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600" />
          <div className="relative px-6 py-4 flex items-center justify-between">
            <h3 className="font-bold text-white text-lg flex items-center gap-2">
              {editingConfig ? <Settings className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
              {editingConfig ? '编辑配置' : '添加 AI 服务'}
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6 overflow-auto max-h-[65vh]">
          {/* 选择服务商 */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">选择服务商</label>
            <div className="grid grid-cols-4 gap-3">
              {AI_PROVIDERS.map((p) => {
                const isDisabled = !editingConfig && existingProviders.includes(p.id);
                const isSelected = selectedProvider === p.id;
                return (
                  <button key={p.id} onClick={() => !isDisabled && setSelectedProvider(p.id)} disabled={isDisabled}
                    className={`p-4 rounded-xl border-2 transition-all text-center group ${
                      isSelected ? 'border-purple-500 bg-purple-50 shadow-lg shadow-purple-100' 
                        : isDisabled ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}>
                    <div className={`w-12 h-12 mx-auto mb-2 rounded-xl bg-gradient-to-br ${p.gradient} flex items-center justify-center text-2xl shadow-md group-hover:scale-105 transition-transform`}>
                      {p.icon}
                    </div>
                    <div className="text-sm font-semibold text-gray-800">{p.name}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5 truncate">{p.description}</div>
                    {p.popular && <span className="inline-block mt-1 px-1.5 py-0.5 bg-amber-100 text-amber-600 text-[10px] rounded-full">热门</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedProvider && (
            <div className="space-y-5 pt-2">
              {/* API Key */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  API Key <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input type={showKey ? 'text' : 'password'} value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm transition-all" />
                  <button type="button" onClick={() => setShowKey(!showKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {selectedProvider === 'minimax' && (
                <div className="space-y-2">
                  <p className="text-xs text-violet-900 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2 leading-relaxed">
                    已切换为 MiniMax 官方 <strong>OpenAI 兼容</strong>接口（<code className="text-xs bg-white/80 px-1 rounded">/v1/chat/completions</code>），仅需在开放平台「接口密钥」创建的 API Key，<strong>无需 Group ID</strong>。
                    默认地址 <code className="text-xs bg-white/80 px-1 rounded">https://api.minimaxi.com/v1</code>，亦可留空端点使用默认值。
                    <a
                      href="https://platform.minimaxi.com/docs/api-reference/text-chat-openai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-1 text-indigo-600 hover:underline"
                    >
                      接口说明
                    </a>
                  </p>
                  <button
                    type="button"
                    onClick={handleSaveMinimaxServerOnly}
                    disabled={minimaxServerSaving || !apiKey.trim()}
                    className="w-full px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    <Server className="w-4 h-4" />
                    {minimaxServerSaving ? '写入中…' : '保存 MiniMax 到服务器'}
                  </button>
                  <p className="text-xs text-gray-500">写入服务端 ai-minimax-config.json，供舆情与后端任务使用。</p>
                </div>
              )}

              {/* Base URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  API 端点
                  {selectedProvider !== 'custom' && <span className="text-gray-400 font-normal ml-2">(可选，留空使用默认)</span>}
                </label>
                <input type="text" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={provider?.baseUrl || 'https://api.example.com/v1'}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm transition-all" />
              </div>

              {/* 模型选择 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">模型</label>
                {selectedProvider === 'volcano' ? (
                  <>
                    <p className="text-xs text-gray-500 mb-2">火山引擎需填写<strong>推理接入点 ID</strong>（在 <a href="https://console.volcengine.com/ark" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">控制台</a> → 模型推理 → 推理接入点 中创建后复制 ep-xxx）</p>
                    <input type="text" value={model} onChange={(e) => setModel(e.target.value)}
                      placeholder="ep-20241118110230-xxxxxxxx"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-mono transition-all" />
                  </>
                ) : selectedProvider === 'custom' ? (
                  <input type="text" value={customModels} onChange={(e) => setCustomModels(e.target.value)}
                    placeholder="模型名称，多个用逗号分隔"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm transition-all" />
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {provider?.models?.map((m) => (
                      <button key={m} onClick={() => setModel(m)}
                        className={`px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all text-left ${
                          model === m ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-purple-300 text-gray-600'
                        }`}>
                        <Cpu className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
                        {m}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/80">
          <button onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl transition-colors">
            取消
          </button>
          <button onClick={handleSave} disabled={!selectedProvider || !apiKey.trim() || (selectedProvider === 'volcano' && !model.trim())}
            className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-200 flex items-center gap-2">
            <Check className="w-4 h-4" />
            {editingConfig ? '保存修改' : '添加服务'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIAPIConfig;
